import { IndexedExpression } from '.';

export const applyGroupPostOperator = ({ expressions, index }: IndexedExpression): IndexedExpression => {
  const groupStack = [] as number[];
  let groupSearchIndex = index + 2;
  while (groupSearchIndex < expressions.length) {
    const searchExpression = expressions[groupSearchIndex];
    if (searchExpression.operator === 'group_end' || searchExpression.operator === 'not_end') {
      if (groupStack.length === 0) {
        break;
      }
      groupStack.pop();
    }
    if (searchExpression.operator === 'group_start' || searchExpression.operator === 'not_start') {
      groupStack.push(searchExpression.groupIndex);
    }

    groupSearchIndex += 2;
  }
  groupSearchIndex += 2;

  expressions.splice(index + 1, 0, { operator: 'position' }, { groupIndex: 99999, operator: 'group_start' });

  expressions.splice(
    groupSearchIndex > expressions.length ? expressions.length : groupSearchIndex,
    0,
    { groupIndex: 99999, operator: 'group_end' },
    { operator: 'position' },
  );
  return { expressions, index };
};

const applyGroupPreOperator = ({ expressions, index }: IndexedExpression) => {
  const groupStack = [] as number[];
  let groupSearchIndex = index - 2;
  while (groupSearchIndex >= 0) {
    const searchExpression = expressions[groupSearchIndex];
    if (searchExpression.operator === 'group_start' || searchExpression.operator === 'not_start') {
      if (groupStack.length === 0) {
        break;
      }
      groupStack.pop();
    }
    if (searchExpression.operator === 'group_end' || searchExpression.operator === 'not_end') {
      groupStack.push(searchExpression.groupIndex);
    }

    groupSearchIndex -= 2;
  }
  expressions.splice(index - 1, 0, { operator: 'position' }, { groupIndex: 99999, operator: 'group_end' });

  expressions.splice(
    groupSearchIndex < 0 ? 0 : groupSearchIndex - 1,
    0,
    { operator: 'position' },
    { groupIndex: 99999, operator: 'group_start' },
  );
  return { expressions, index };
};

export const getEndOfGroup = ({ expressions, index }: IndexedExpression) => {
  let groupCount = 0;
  for (let i = index + 1; i < expressions.length; ++i) {
    const expression = expressions[i];
    switch (expression.operator) {
      case 'group_end':
      case 'not_end': {
        if (groupCount === 0) {
          return i;
        }
        --groupCount;
        break;
      }
      case 'group_start':
      case 'not_start':
        ++groupCount;
        break;
    }
  }
  return expressions.length;
};

export const getRightOperator = (
  { expressions, index }: IndexedExpression,
  operator: 'or' | 'and',
  displayOr: boolean,
  PrevExpression = false,
): { index: number; operator: 'group_end' | 'not_end' | 'or' | 'and' | undefined } => {
  const expression = expressions[index + 2];
  if (expression) {
    if (expression.operator === operator) {
      return getRightOperator({ expressions, index: index + 2 }, operator, displayOr, false);
    }
    switch (expression.operator) {
      case 'expression':
        if (displayOr === false && PrevExpression) {
          return { index: index + 2, operator: 'or' };
        } else {
          return getRightOperator({ expressions, index: index + 2 }, operator, displayOr, true);
        }

      case 'group_start':
      case 'not_start': {
        if (displayOr === false && PrevExpression) {
          return { index: index + 2, operator: 'or' };
        }
        const endIndex = getEndOfGroup({ expressions, index: index + 2 });
        return getRightOperator({ expressions, index: endIndex }, operator, displayOr, true);
      }
      case 'or':
      case 'and':
      case 'group_end':
      case 'not_end':
        return { index: index + 2, operator: expression.operator };
    }
  }
  return { index: index + 2, operator: undefined };
};

export const getStartOfGroup = ({ expressions, index }: IndexedExpression) => {
  const expression = expressions[index];
  const groupId = (expression as { id?: string; groupIndex: number; operator: 'group_start' }).groupIndex;
  if (groupId === undefined) {
    let groupCount = 0;
    for (let i = index - 1; i >= 0; --i) {
      const expression = expressions[i];
      switch (expression.operator) {
        case 'group_end':
        case 'not_end': {
          ++groupCount;
          break;
        }
        case 'group_start':
        case 'not_start':
          if (groupCount === 0) {
            return i;
          }
          --groupCount;
          break;
      }
    }
  } else {
    for (let i = index - 1; i >= 0; --i) {
      const expression = expressions[i];
      switch (expression.operator) {
        case 'group_start':
        case 'not_start': {
          if (expression.groupIndex === groupId) {
            return i;
          }
        }
      }
    }
  }
  return 0;
};

export const getLeftOperator = (
  { expressions, index }: IndexedExpression,
  operator: 'or' | 'and',
  displayOr: boolean,
  PrevExpression = false,
): { index: number; operator: 'group_start' | 'not_start' | 'or' | 'and' | undefined } => {
  const expression = expressions[index - 2];
  if (expression) {
    if (expression.operator === operator) {
      return getLeftOperator({ expressions, index: index - 2 }, operator, displayOr, false);
    }
    switch (expression.operator) {
      case 'expression':
        if (displayOr === false && PrevExpression) {
          return { index: index - 2, operator: 'or' };
        } else {
          return getLeftOperator({ expressions, index: index - 2 }, operator, displayOr, true);
        }
      case 'or':
      case 'and':
      case 'group_start':
      case 'not_start':
        return { index: index - 2, operator: expression.operator };
      case 'group_end':
      case 'not_end': {
        if (displayOr === false && PrevExpression) {
          return { index: index - 2, operator: 'or' };
        }
        const startIndex = getStartOfGroup({ expressions, index: index - 2 });
        return getLeftOperator({ expressions, index: startIndex }, operator, displayOr, true);
      }
    }
  }
  return { index: index - 2, operator: undefined };
};

export const removeGroupAfterOperator = ({ expressions, index }: IndexedExpression): IndexedExpression => {
  const expression = expressions[index + 2];
  switch (expression.operator) {
    case 'group_start': {
      const endLocation = getEndOfGroup({ expressions, index: index + 2 });
      expressions.splice(endLocation - 1, 2);
      expressions.splice(index + 1, 2);
      return { expressions, index };
    }
    default:
      return { expressions, index };
  }
};

export const removeGroupBeforeOperator = ({ expressions, index }: IndexedExpression): IndexedExpression => {
  const expression = expressions[index - 2];
  switch (expression.operator) {
    case 'group_end': {
      const startLocation = getStartOfGroup({ expressions, index: index - 2 });
      expressions.splice(index - 3, 2);
      expressions.splice(startLocation - 1, 2);
      return { expressions, index: index - 4 };
    }
    default:
      return { expressions, index };
  }
};

export const groupOperator = (
  { expressions, index }: IndexedExpression,
  displayOr: boolean,
  excludeAnd: boolean,
  secondRun = false,
): IndexedExpression => {
  if (excludeAnd === true) return { expressions, index };
  const expressionOperator = expressions[index].operator;
  if (expressionOperator === 'expression' && displayOr === false) {
    expressions.splice(
      index,
      0,
      {
        operator: 'or',
      },
      { operator: 'position' },
    );
  }
  const operator = expressions[index].operator;
  const leftOperatorType = getLeftOperator({ expressions, index }, expressionOperator as 'or' | 'and', displayOr);
  const rightOperatorType = getRightOperator({ expressions, index }, expressionOperator as 'or' | 'and', displayOr);

  const leftOperator =
    leftOperatorType.operator === 'or' || leftOperatorType.operator === 'and' ? leftOperatorType.operator : undefined;
  const rightOperator =
    rightOperatorType.operator === 'or' || rightOperatorType.operator === 'and'
      ? rightOperatorType.operator
      : undefined;

  const leftGroup = (leftOperator ?? operator) !== operator;
  const rightGroup = (rightOperator ?? operator) !== operator;
  if (leftGroup && rightGroup) {
    // A or B *and* C or D => ( A or B ) *and* ( C or D )
    return {
      expressions: applyGroupPreOperator(applyGroupPostOperator({ expressions, index })).expressions,
      index: index + 4,
    };
  } else if (leftGroup) {
    // A or B *and* C => ( A or B ) *and* C
    return {
      expressions: applyGroupPreOperator({ expressions, index }).expressions,
      index: index + 4,
    };
  } else if (rightGroup) {
    // A *and* B or C => A *and* ( B or C )
    return {
      expressions: applyGroupPostOperator({ expressions, index }).expressions,
      index: index,
    };
  } else {
    if (secondRun) {
      return {
        expressions: expressions,
        index: index,
      };
    } else if (
      (leftOperatorType.operator === 'group_start' || leftOperatorType.operator === 'not_start') &&
      (rightOperatorType.operator === 'group_end' || rightOperatorType.operator === 'not_end')
    ) {
      const leftOperatorTypeGroup = getLeftOperator(
        { expressions, index: leftOperatorType.index },
        expressionOperator as 'or' | 'and',
        displayOr,
      );
      const rightOperatorTypeGroup = getRightOperator(
        { expressions, index: rightOperatorType.index },
        expressionOperator as 'or' | 'and',
        displayOr,
      );
      const leftOperatorGroup =
        leftOperatorTypeGroup.operator === 'or' || leftOperatorTypeGroup.operator === 'and'
          ? leftOperatorType.operator
          : undefined;
      const rightOperatorGroup =
        rightOperatorTypeGroup.operator === 'or' || rightOperatorTypeGroup.operator === 'and'
          ? rightOperatorType.operator
          : undefined;

      const leftUngroup = (leftOperatorGroup ?? operator) === operator;
      const rightUngroup = (rightOperatorGroup ?? operator) === operator;

      if (leftUngroup && rightUngroup) {
        // A and ( B *and* C ) and D => A and B *and* C and D
        // A or (( B *and* C ) or D ) => A or ( B *and* C ) or D // like to have
        // A or (( B *and* C ) and D ) => A or ( B *and* C and D)
        // A or ( B *and* C ) and D => A or ( B *and* C and D ) // like to have
        // A or ( B *and* ( C and D ))  => A or ( B *and* C and D) // like to have
        // A and ( B *and* C ) or D => ( A and B *and* C ) or D // like to have
        const result = removeGroupAfterOperator({ expressions, index: leftOperatorType.index - 2 });
        return groupOperator({ expressions: result.expressions, index: index - 2 }, displayOr, excludeAnd);
      } else {
        return {
          expressions: expressions,
          index: index,
        };
      }
    } else {
      // ( A ) *and* ( B ) => A *and* B
      // A and ( B ) *and* ( C ) => A and B *and* C
      return groupOperator(
        removeGroupBeforeOperator(removeGroupAfterOperator({ expressions, index })),
        displayOr,
        excludeAnd,
        true,
      );
    }
  }
};
